import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Loader2 } from 'lucide-react';
import { projects as staticProjects } from '../data/projects';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { projectsApi } from '../services/api';

export default function PortfolioPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectsApi.getAll()
      .then(res => setProjects(res.data || res || []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const completedProjects = projects.filter((p) => p.status === 'Completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-20">
        <Loader2 className="w-10 h-10 text-[#47ace2] animate-spin" />
      </div>
    );
  }

  const f = (proj, key1, key2) => proj[key1] !== undefined ? proj[key1] : proj[key2];

  return (
    <div data-testid="portfolio-page" className="pt-20">
      {/* Header */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
            Our Work
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 font-outfit mt-4">
            Completed Projects
          </h1>
          <p className="text-lg text-slate-600 font-manrope mt-4 max-w-2xl">
            Explore our portfolio of successfully delivered projects. Each project 
            represents our commitment to quality, innovation, and customer satisfaction.
          </p>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {completedProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {completedProjects.map((project, index) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  data-testid={`portfolio-${project.id}`}
                  className={`group relative overflow-hidden rounded-2xl ${
                    index === 0 ? 'md:col-span-2 aspect-[21/9]' : 'aspect-[4/3]'
                  }`}
                >
                  <img
                    src={project.images?.[0] || 'https://images.unsplash.com/photo-1549665600-42c9e852661e?w=800&q=80'}
                    alt={project.name}
                    className="w-full h-full object-contain bg-slate-50 group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <Badge className="bg-emerald-500 text-white mb-4">Completed</Badge>
                    <h3 className="text-2xl md:text-3xl font-semibold text-white font-outfit mb-2">
                      {project.name}
                    </h3>
                    <p className="text-white/80 font-manrope mb-4">
                      {project.location} • {f(project, 'completionDate', 'completion_date')}
                    </p>
                    <div className="flex items-center gap-6 text-white/70 font-manrope text-sm">
                      <span>{project.type}</span>
                      <span>•</span>
                      <span>{f(project, 'totalUnits', 'total_units')} Units</span>
                      <span>•</span>
                      <span>{project.category}</span>
                    </div>
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-[#47ace2]/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Eye className="w-12 h-12 mx-auto mb-4" />
                      <span className="text-lg font-semibold font-outfit">View Project</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 font-manrope text-lg">
                Portfolio coming soon.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <span className="block text-4xl md:text-5xl font-light text-[#47ace2] font-outfit">
                50+
              </span>
              <span className="text-slate-400 font-manrope mt-2 block">Projects Completed</span>
            </div>
            <div>
              <span className="block text-4xl md:text-5xl font-light text-[#47ace2] font-outfit">
                5000+
              </span>
              <span className="text-slate-400 font-manrope mt-2 block">Happy Families</span>
            </div>
            <div>
              <span className="block text-4xl md:text-5xl font-light text-[#47ace2] font-outfit">
                2.5M
              </span>
              <span className="text-slate-400 font-manrope mt-2 block">Sq.ft Delivered</span>
            </div>
            <div>
              <span className="block text-4xl md:text-5xl font-light text-[#47ace2] font-outfit">
                25+
              </span>
              <span className="text-slate-400 font-manrope mt-2 block">Years of Excellence</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#F0F8FF]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-light tracking-tight text-slate-900 font-outfit mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-slate-600 font-manrope mb-8">
            Explore our current projects or get in touch with our team to discuss your requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/projects">
              <Button className="bg-[#47ace2] hover:bg-[#1f7db3] rounded-full px-8">
                View Current Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-[#47ace2] text-[#47ace2] hover:bg-white rounded-full px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
